import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

export default function Login() {
  const { login, user, loading } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!loading && user) navigate("/", { replace: true });
  }, [user, loading, navigate]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    const res = await login(email, password);
    setSubmitting(false);
    if (res.ok) {
      toast.success("Welcome back to the studio");
      navigate("/");
    } else {
      setError(res.error || "Login failed");
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-canvas">
      {/* Left branding */}
      <div className="hidden md:block relative grain">
        <img
          src="https://images.pexels.com/photos/7301210/pexels-photo-7301210.jpeg"
          alt="Studio microphone"
          className="absolute inset-0 w-full h-full object-cover opacity-40"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-canvas via-transparent to-canvas/80" />
        <div className="relative z-10 flex flex-col justify-between h-full p-12">
          <div className="flex items-center gap-3">
            <div>
              <div className="font-heading font-black text-2xl leading-none">PodOS</div>
              <div className="ui-label mt-1">Studio&nbsp;PM</div>
            </div>
          </div>
          <div>
            <div className="ui-label">Broadcast Studio · Production OS</div>
            <h1 className="font-heading text-5xl font-black tracking-tight mt-4 leading-[1.05]">
              Run every episode<br />like it's live.
            </h1>
            <p className="text-fg2 mt-4 max-w-md">
              A control room for your podcast production pipeline — from pitch to publish.
              Kanban boards, deep task trees, and dashboards built for producers.
            </p>
          </div>
        </div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-8">
        <form
          onSubmit={onSubmit}
          className="w-full max-w-sm space-y-6"
          data-testid="login-form"
        >
          <div>
            <div className="ui-label">Sign in</div>
            <h2 className="font-heading text-3xl font-bold mt-2">Enter the studio</h2>
            <p className="text-sm text-fg2 mt-2">
              Use your team credentials. Access is invite-only.
            </p>
          </div>

          <div className="space-y-4">
            <div>
              <Label htmlFor="email" className="ui-label">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@studio.com"
                data-testid="login-email-input"
                className="mt-2 bg-surface border-line focus-visible:ring-[#FF3B30]/50"
                required
              />
            </div>
            <div>
              <Label htmlFor="password" className="ui-label">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password-input"
                className="mt-2 bg-surface border-line focus-visible:ring-[#FF3B30]/50"
                required
              />
            </div>
          </div>

          {error && (
            <div
              data-testid="login-error"
              className="text-sm text-[#FF3B30] bg-[#FF3B30]/10 border border-[#FF3B30]/20 rounded-md px-3 py-2"
            >
              {error}
            </div>
          )}

          <Button
            type="submit"
            disabled={submitting}
            data-testid="login-submit-button"
            className="w-full bg-[#FF3B30] hover:bg-[#D63026] text-white font-medium h-11"
          >
            {submitting ? "Signing in..." : "Sign in"}
          </Button>
        </form>
      </div>
    </div>
  );
}
